import type { GeneratePresignedUrlModel } from '@/shared/api'
import { notifyMaxLengthExceeded } from '@/shared/lib/form-utils'
import { type UploadFile } from 'antd'
import { createEffect, scopeBind } from 'effector'

import {
  UploadFileType,
  presignedUrlFx,
  uploadFileToPresignedUrlFx,
} from '../api'

export type UploadFileWithId = UploadFile & {
  id?: string
}

export type UploadFileToPresignedUrlPayload = GeneratePresignedUrlModel & {
  file: File
  uid: string
  aspectRatio?: number
  fileStartUpload: (payload: Pick<UploadFileWithId, 'uid' | 'id'>) => void
  fileProgressChanged: (
    payload: Pick<UploadFileWithId, 'uid' | 'percent'>,
  ) => void

  fileUploadSuccess: (payload: Pick<UploadFileWithId, 'uid' | 'url'>) => void
  fileUploadError: (payload: Pick<UploadFileWithId, 'uid' | 'error'>) => void
}
export const uploadFileToPresignedUrl = createEffect(
  async ({
    uid,
    fileName,
    fileType,
    duration,
    aspectRatio,
    dominantColor,
    file,
    fileStartUpload,
    fileProgressChanged,
    fileUploadSuccess,
    fileUploadError,
  }: UploadFileToPresignedUrlPayload) => {
    try {
      const { fileInfo, presignedUrl } = await presignedUrlFx({
        data: { fileName, fileType, duration, aspectRatio, dominantColor },
      })

      fileStartUpload({ uid, id: fileInfo.id })

      await uploadFileToPresignedUrlFx({
        url: presignedUrl,
        file,
        onUploadProgress: (percent) =>
          scopeBind(fileProgressChanged)({ uid, percent }),
      })

      if (fileType === UploadFileType.Image && fileInfo.fullCdnUrl) {
        await waitForImageToBeAvailable(fileInfo.fullCdnUrl)
      }

      fileUploadSuccess({
        uid,
        url: fileInfo.fullCdnUrl,
      })

      return fileInfo
    } catch (error) {
      notifyMaxLengthExceeded(error)
      fileUploadError({ uid, error })
      throw error
    }
  },
)

export function waitForImageToBeAvailable(
  url: string,
  signal?: AbortSignal,
  maxRetries = 5,
  delayMs = 1000,
): Promise<void> {
  return new Promise((resolve, reject) => {
    let attempts = 0

    const check = () => {
      if (signal?.aborted) {
        reject(new Error('Aborted by signal'))
        return
      }

      const img = new Image()
      img.onload = () => {
        resolve()
      }
      img.onerror = () => {
        attempts++
        if (attempts >= maxRetries) {
          reject(new Error('Image not available on CDN after multiple retries'))
        } else {
          setTimeout(check, delayMs)
        }
      }
      img.src = url
    }

    check()
  })
}
